// What the "Request leave" form tells you about the dates you've picked: how many working days they are, whether they
// clash with something already booked, what's left of the allowance afterwards and who will be asked to approve.
// Pure, so it can be tested without the form.

import type { HolidayMap, LeaveRequest, RequestDraft, Settings, User } from '../types';
import { fmtRange, formatDays, leaveYearLabel } from './dates';
import {
  allowanceFor, approversOf, balanceFor, findConflict, LEAVE_TYPES, requestDays, requestDaysByLeaveYear,
} from './leave';

export interface BookingContext {
  me: User;
  users: User[];
  requests: LeaveRequest[];
  settings: Settings;
  bh: HolidayMap;
}

export interface BookingNote {
  text: string;
  /** Worth the person's attention, but doesn't stop them sending the request. */
  warn?: boolean;
}

export interface BookingCheck {
  /** Working days the request covers (0 while there are problems). */
  days: number;
  /** Reasons the request can't be sent. */
  problems: string[];
  notes: BookingNote[];
}

const days = (n: number) => `${formatDays(n)} day${n === 1 ? '' : 's'}`;

export function checkBooking(draft: RequestDraft, ctx: BookingContext): BookingCheck {
  const problems: string[] = [];
  const notes: BookingNote[] = [];
  const { me, settings } = ctx;
  const user = ctx.users.find((u) => u.id === draft.userId) ?? me;
  const isMe = user.id === me.id;
  const type = LEAVE_TYPES[draft.typeId];

  if (!draft.start || !draft.end) problems.push('Choose a start and end date.');
  else if (draft.end < draft.start) problems.push('The end date is before the start date.');
  if (problems.length) return { days: 0, problems, notes };

  const total = requestDays(draft, ctx.bh);
  if (total === 0) problems.push('That range has no working days in it (weekends and bank holidays are not counted).');
  const clash = findConflict(user.id, draft, ctx.requests, ctx.bh);
  if (clash) problems.push(`${isMe ? 'You already have' : `${user.name} already has`} leave on those dates (${fmtRange(clash.start, clash.end)}).`);
  if (problems.length) return { days: 0, problems, notes };

  if (type.deducts) {
    for (const [year, used] of Object.entries(requestDaysByLeaveYear(draft, ctx.bh, settings.leaveYearStartMonth))) {
      const left = balanceFor(user, Number(year), ctx).remaining - used;
      const label = leaveYearLabel(Number(year), settings.leaveYearStartMonth);
      notes.push(
        left < 0
          ? { warn: true, text: `This goes ${days(-left)} over ${isMe ? 'your' : 'their'} ${label} allowance (${formatDays(allowanceFor(user, settings, Number(year)))} days).` }
          : { text: `${days(left)} left in ${label} after this.` },
      );
    }
  } else {
    notes.push({ text: `${type.label} does not use up holiday allowance.` });
  }

  if (!type.needsApproval) {
    notes.push({ text: 'Recorded immediately; no approval needed.' });
  } else if (isMe) {
    const { leads, hr } = approversOf(user, ctx);
    const names = [...leads, ...hr.filter((h) => !leads.includes(h))].map((u) => u.name);
    notes.push(
      names.length
        ? { text: `Goes to ${names.join(', ')} for approval.` }
        : { warn: true, text: 'Nobody else is set up to approve your leave yet. Ask HR to put you in a team with a team lead or add another HR user.' },
    );
  }

  return { days: total, problems, notes };
}
